import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

/* ── Tip client ─────────────────────────────────────────────────────────── */
export interface ClientUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  provider: 'email' | 'google' | 'facebook' | 'linkedin' | 'instagram';
  created_at: string;
}

interface AuthCtx {
  user: ClientUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, phone?: string) => Promise<void>;
  loginWithOAuth: (provider: string, profile: { name: string; email: string; avatar?: string }) => Promise<void>;
  logout: () => Promise<void>;
  requestReset: (email: string) => Promise<string>;
  confirmReset: (token: string, password: string) => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

const API = '/auth.php';
const TOKEN_KEY = 'interp_client_token';

async function apiFetch(action: string, body: object = {}) {
  const res = await fetch(`${API}?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...body }),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || 'Eroare server');
  return data;
}

async function apiGet(action: string, token: string) {
  const res = await fetch(`${API}?action=${action}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || 'Eroare server');
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]   = useState<ClientUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /* Restore session on mount */
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      apiGet('me', t)
        .then(d => { setUser(d.client); setToken(t); })
        .catch(() => localStorage.removeItem(TOKEN_KEY))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const saveSession = (t: string, c: ClientUser) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t); setUser(c);
  };

  const login = async (email: string, password: string) => {
    const d = await apiFetch('login', { email, password });
    saveSession(d.token, d.client);
  };

  const register = async (name: string, email: string, password: string, phone = '') => {
    const d = await apiFetch('register', { name, email, password, phone });
    saveSession(d.token, d.client);
  };

  const loginWithOAuth = async (provider: string, profile: { name: string; email: string; avatar?: string }) => {
    const d = await apiFetch('oauth-callback', { provider, profile });
    saveSession(d.token, d.client);
  };

  const logout = async () => {
    if (token) {
      try { await fetch(`${API}?action=logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); }
      catch { /* ignore */ }
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null); setUser(null);
  };

  const requestReset = async (email: string): Promise<string> => {
    const d = await apiFetch('reset-request', { email });
    return d.message;
  };

  const confirmReset = async (resetToken: string, password: string) => {
    await apiFetch('reset-confirm', { token: resetToken, password });
  };

  return (
    <Ctx.Provider value={{ user, token, loading, login, register, loginWithOAuth, logout, requestReset, confirmReset }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
